import AxiosInstance from '@/api/axios';
import { toast } from '@/components/Base/Toast/ToastManager';
import { loginSuccess } from '@/utils/helper';
import { CustomError, UserLoginResponseType } from '@/utils/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface LoginBodyType {
  email: string;
  password: string;
}

export const login = createAsyncThunk(
  'login/postData',
  async ({ email, password }: LoginBodyType, thunkAPI) => {
    try {
      const response = await AxiosInstance.post(`/auth/login`, {
        email,
        password,
      });

      const { data: loginData, success, message } = response.data;

      // redirect if saml user
      if (loginData?.type === 'saml' && loginData.redirectUrl) {
        window.location.href = loginData.redirectUrl;
        return;
      }

      if (success) {
        if (loginData?.user) {
          loginSuccess(loginData.user.role, loginData.user.organization_id);
        }

        return response.data;
      }

      toast.show({
        title: 'Operation Failed',
        content: (message as string) || 'Login failed. Please try again.',
        duration: 2000,
        type: 'warning',
      });
      return thunkAPI.rejectWithValue('Login failed.');
    } catch (error) {
      const customError = error as CustomError;
      toast.show({
        title: 'Operation Failed',
        content: customError?.response?.data?.message as string,
        duration: 2000,
        type: 'warning',
      });
      return thunkAPI.rejectWithValue('An error occurred during login.');
    }
  }
);

export const getClientTeam = createAsyncThunk(
  'getClientTeam/fetchData',
  async (_, thunkAPI) => {
    try {
      const response = await AxiosInstance.get(`/client-teams`);
      if (response.data && response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Login API call error:', error);
      return thunkAPI.rejectWithValue('An error occurred during login.');
    }
  }
);

interface LoginStateType {
  user: UserLoginResponseType | null | undefined;
  loading: boolean;
  error: string | null;
}

const initialState: LoginStateType = {
  user: null,
  loading: false,
  error: null,
};

const loginReducer = createSlice({
  name: 'login',
  initialState,
  reducers: {
    setMediaPath: (state, action) => {
      if (state.user) {
        if (!state.user.data.user.media) {
          state.user.data.user.media = { path: '' };
        }

        state.user.data.user.media.path = action.payload;
      }
    },
    // Direct user-state setter. Used by the dev auth seed (see
    // src/utils/devAuthSeed.ts) to populate Redux with a fake admin
    // when there's no Antares Backend running locally. Production-safe:
    // the seed itself is gated on import.meta.env.DEV.
    setUser: (state, action) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getClientTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClientTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getClientTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setMediaPath, setUser } = loginReducer.actions;

export default loginReducer.reducer;
